import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { classifyRead, ReadState } from "../../_shared/readState";

export const IS_ELEVATED_ADMIN_QUERY_KEY = ['is-elevated-admin'];

export const useIsElevatedAdmin = () => {
    const apiClient = useApiClient();
    return useQuery({
        queryKey: IS_ELEVATED_ADMIN_QUERY_KEY,
        queryFn: async () => {
            const result = await apiClient.fetchApi<{ isElevatedAdmin: boolean }, {}>({
                method: 'GET',
                endpoint: 'organizations/is-elevated-admin',
            });
            return result.isElevatedAdmin;
        },
        staleTime: Infinity,
    });
};

/**
 * What we know about the current admin's elevation, as far as the UI is
 * concerned.
 *
 * This is the campaign's `ReadState` narrowed to a PERMISSION read, and it
 * exists because a permission is the one payload where the campaign's usual
 * remedy is unavailable. Everywhere else, the honest answer to "the read did
 * not come back" is to say so and let the operator retry. Here, four of the
 * five consumers were rendering that state as `return null` and the fifth as a
 * sentence — and both are DENIALS, stated about a question the API never
 * answered.
 *
 * `denied` and `unknown` are therefore kept apart on purpose:
 *
 *   elevated    the API answered `true`. The only state that grants anything.
 *   denied      the API answered `false`. A real answer; hiding the surface is
 *               the correct render and stays exactly as it was.
 *   checking    the read is genuinely in flight. Transient, resolves.
 *   unknown     we asked and did not get an answer (or, defensively, never
 *               asked). NOT a denial — and not a grant either.
 *
 * The API's own shape is what makes the distinction real rather than
 * theoretical: `GET organizations/is-elevated-admin` sits behind
 * `AdminAuthGuard` and returns `{ isElevatedAdmin: boolean }` for every
 * authenticated admin (`api/src/organization/organization.controller.ts:160`).
 * It does NOT 403 an ordinary operator — it answers `false`. So `isError` here
 * never means "you are not elevated"; it means the question did not get
 * through, which is a different thing and, with `staleTime: Infinity` above,
 * one that persists for the rest of the session.
 */
export type ElevationState = 'elevated' | 'denied' | 'checking' | 'unknown';

/**
 * Map a read state onto an elevation state. FAIL-CLOSED by construction: this
 * is the only place in the dashboard that can produce `'elevated'`, and it does
 * so from exactly one input pair — an ANSWERED read holding literal `true`.
 *
 * Pinned by `elevated-admin-read-state.test.tsx`, which asserts the property
 * over the whole 4 x 3 input space rather than over the cases a page happens to
 * mount: no combination other than `('answered', true)` may grant.
 *
 * `'never-issued'` is unreachable today — `useIsElevatedAdmin` carries no
 * `enabled:` gate, so React Query never parks it. It is mapped to `'unknown'`
 * rather than left to fall through, because the day someone adds a gate the
 * safe answer is "we do not know", not "no".
 */
export function classifyElevation(
    readState: ReadState,
    isElevatedAdmin: boolean | undefined,
): ElevationState {
    if (readState === 'answered') {
        return isElevatedAdmin === true ? 'elevated' : 'denied';
    }
    if (readState === 'loading') return 'checking';
    return 'unknown';
}

/**
 * The hook every elevation-gated surface should call.
 *
 * `useIsElevatedAdmin()` is still exported above and still returns the raw
 * query, but reading `data` off it directly is what produced this class: five
 * surfaces wrote `const { data: isElevatedAdmin } = useIsElevatedAdmin()` and
 * then branched on `!isElevatedAdmin`, which collapses `undefined` into
 * `false`. `isElevated` here is a real boolean that is true ONLY for an
 * answered `true`, so the fail-closed half is identical to what those five did
 * — and `state` is the part they could not express.
 *
 * Note this issues no extra request: it is the same query key, served from the
 * same cache, so a page and the cards inside it can both call it.
 */
export const useElevatedAdminAccess = () => {
    const { data, isError, isInitialLoading } = useIsElevatedAdmin();
    const state = classifyElevation(
        classifyRead(isError, isInitialLoading, data),
        data,
    );
    return { state, isElevated: state === 'elevated' };
};
